import React from "react";
import { useSearchParams } from "react-router-dom";
import LoginForm from "../components/LoginForm";
import { cn } from "../lib/utils";

// Error codes surfaced via `?error=<code>` query param (e.g. set by the backend
// when SSO sign-in is rejected). Add new entries here as needed.
const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  signup_disabled:
    "To sign in with SSO, your account must first be created by an administrator. Please contact us at business@peerception.io for more information.",
};

const Login = ({ login_succeeds, on_click_register, login_to_validation, report_forgotten_password }) => {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const errorMessage = errorCode ? LOGIN_ERROR_MESSAGES[errorCode] : null;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-10 pb-6 text-center">
            <img
              width="220"
              height="68"
              src="/logo-text-black.png"
              alt="Peerception"
              className="mx-auto mb-6"
            />
            <h1 className="text-xl font-bold text-slate-900">Welcome</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to your account to continue</p>
          </div>

          {/* Body */}
          <div className="px-8 pb-8">
            {/* Error banner (driven by `?error=<code>` query param) */}
            {errorMessage && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200">
                <span className="material-symbols-outlined text-[18px] shrink-0 mt-0.5">error</span>
                <span className="text-left">{errorMessage}</span>
              </div>
            )}

            {/* Microsoft SSO */}
            <a
              href={process.env.REACT_APP_API_URL + '/login/microsoft'}
              className={cn(
                "flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl",
                "border-2 border-slate-200 bg-white text-slate-700",
                "hover:border-slate-300 hover:bg-slate-50 transition-all",
                "text-sm font-semibold"
              )}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Microsoft_logo.svg/1280px-Microsoft_logo.svg.png"
                alt="Microsoft"
                width="20"
                height="20"
              />
              Continue with Microsoft
            </a>

            {/* Divider */}
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Login Form */}
            <LoginForm
              login_succeeds={login_succeeds}
              login_to_validation={login_to_validation}
              report_forgotten_password={report_forgotten_password}
            />
          </div>
        </div>

        {/* Footer — hidden when signup is disabled (no `on_click_register` handler provided) */}
        {on_click_register && (
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Don't have an account?
            </p>
            <button
              onClick={() => on_click_register()}
              className="mt-2 text-sm font-semibold text-primary hover:text-blue-700 transition-colors"
            >
              Register with email
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
